import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect } from 'react';
import { DialogTrigger } from 'react-aria-components';
import { useParams } from 'react-router-dom';

import {
	Button,
	Drawer,
	Group,
	Stack,
	Tabs,
} from '@ekhodealer/ekho-common/rev';

import type { TabItem } from '@ekhodealer/ekho-common/rev';
import type { PreorderDetailsTab } from '../atoms/preorderDetailsDrawerAtoms';

import {
	ChevronDown24,
	ChevronUp24,
} from '@common/components/icons';

import { useSearchPreorders } from '../api/useSearchPreorders';
import {
	PREORDER_DETAILS_TAB,
	preorderDetailsDrawerAtom,
} from '../atoms/preorderDetailsDrawerAtoms';
import { preordersParamsAtom, preorderTabFilterAtom } from '../atoms/preordersAtoms';
import { usePreorderNavigation } from '../hooks/usePreorderNavigation';

import { DetailsTabContent } from './preorder-details/DetailsTabContent';
import { PREORDER_ACTIONS_CONTEXT, PreorderActions } from './PreorderActions';

import '@common/components/_drawerShared.scss';

export const PreorderDetailsDrawer = (): React.ReactNode => {
	// Get URL params
	const { preorderId, detailsTab } = useParams<{
		preorderId?: string;
		detailsTab?: string;
	}>();
	const {
		closePreorderDetails,
		navigateToPreviousPreorder,
		navigateToNextPreorder,
		getNavigationState,
	} = usePreorderNavigation();

	// Drawer is open if preorderId and detailsTab are present in URL
	const isDrawerOpen = Boolean(preorderId && detailsTab);
	const activeTab = (detailsTab as PreorderDetailsTab) ?? PREORDER_DETAILS_TAB.DETAILS;

	// Get preorders data to find the selected preorder
	const params = useAtomValue(preordersParamsAtom);
	const currentTab = useAtomValue(preorderTabFilterAtom);
	const { data } = useSearchPreorders(params);
	const selectedRow = data?.data.find((p) => p.preorderId === preorderId) ?? null;
	const totalCount = data?.count ?? 0;

	// Get navigation state for prev/next buttons
	const navigationState = preorderId
		? getNavigationState(preorderId, totalCount)
		: { canGoPrevious: false, canGoNext: false };

	// Sync preorderId and selectedRow to atom for tab content components
	const setDrawerState = useSetAtom(preorderDetailsDrawerAtom);
	useEffect(() => {
		setDrawerState({ preorderId: preorderId ?? null, selectedRow });
	}, [preorderId, selectedRow, setDrawerState]);

	// Close drawer handler
	const closeDrawer = (): void => {
		closePreorderDetails();
	};

	const handlePreviousPreorder = (): void => {
		if (preorderId && navigationState.canGoPrevious) {
			navigateToPreviousPreorder(preorderId, activeTab);
		}
	};

	const handleNextPreorder = (): void => {
		if (preorderId && navigationState.canGoNext) {
			navigateToNextPreorder(preorderId, activeTab);
		}
	};

	// Tab items configuration
	const tabItems: TabItem[] = [
		{ id: PREORDER_DETAILS_TAB.DETAILS, content: 'Details' },
	];

	// Render the appropriate content based on the active tab
	const renderTabContent = (): React.ReactNode => {
		switch (activeTab) {
			case PREORDER_DETAILS_TAB.DETAILS:
				return <DetailsTabContent />;
			default:
				return null;
		}
	};

	// Don't render if drawer is not open
	if (!isDrawerOpen) return null;

	const drawerTitle = selectedRow?.buyerFullName
		? `${selectedRow.buyerFullName}'s Preorder`
		: `Preorder ${preorderId?.substring(0, 8) ?? ''}`;

	return (
		<DialogTrigger
			isOpen={isDrawerOpen}
			onOpenChange={(open) => {
				if (!open) closeDrawer();
			}}>
			<Drawer
				placement='right'
				hasCloseButton={true}
				isDismissable={true}
				isKeyboardDismissDisabled={false}
				ariaLabel='Preorder Details'
				className='drawer--custom'>
				<Drawer.Header>{drawerTitle}</Drawer.Header>
				<Drawer.Content padding={{ x: 24, top: 12, bottom: 64 }}>
					<Stack>
						<Tabs
							items={tabItems}
							selectedKey={activeTab}
							onSelect={() => {/* single tab, no-op for now */}}
						/>
						{renderTabContent()}
					</Stack>
				</Drawer.Content>
				<Drawer.Footer>
					<Group itemsSpacing='8' itemsAlignX='space-between' noWrap>
						{selectedRow && (
							<Group.Item>
								<PreorderActions item={selectedRow} currentTab={currentTab} context={PREORDER_ACTIONS_CONTEXT.DRAWER} />
							</Group.Item>
						)}
						<Group.Item>
							<Group itemsSpacing='8' noWrap>
								<Button
									variant='secondary'
									size='icon'
									icon={<ChevronUp24 />}
									aria-label='Previous preorder'
									onPress={handlePreviousPreorder}
									isDisabled={!navigationState.canGoPrevious}
								/>
								<Button
									variant='secondary'
									size='icon'
									icon={<ChevronDown24 />}
									aria-label='Next preorder'
									onPress={handleNextPreorder}
									isDisabled={!navigationState.canGoNext}
								/>
							</Group>
						</Group.Item>
					</Group>
				</Drawer.Footer>
			</Drawer>
		</DialogTrigger>
	);
};
